import { DateAdapter } from "@angular/material/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  HostListener,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from "@angular/router";
import { MatSidenavModule } from "@angular/material/sidenav";
import { TranslocoService, TranslocoModule } from "@ngneat/transloco";
import { ChangeTheme } from "../../../core/services/common/changeTheme.service";
import { OverlayContainer } from "@angular/cdk/overlay";
import { AuthenticationService } from "../../../core/services/common/authentication.service";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatListModule } from "@angular/material/list";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MenuUsuario, RutaDinamicaService } from "src/app/core/services/ruta-dinamica.service";
import { TreeMenuComponent } from "./tree-menu/tree-menu.component";
import { TreeNode } from "@core/services/tree-builder.service";
import { OpenInNewTabDirective } from "@shared/directives/open-in-new-tab.directive";
import { UserStateService } from "@core/services/common/user-state.service";
import { catchError, EMPTY, filter, map, switchMap, tap } from "rxjs";
import { resolverModuloDeRuta } from "./modulo-ruta.utils";

/** Rutas donde no se muestra toolbar ni sidenav */
const EXCLUDED_ROUTES: string[] = [
  "/login",
  "/olvido",
  "/verificar",
  "/work",
];

/** Rutas donde se muestra toolbar pero no sidenav */
const NO_SIDENAV_ROUTES: string[] = [
  "/inicio",
  "/settings",
  "/ayuda",
];

type TamanioPantalla = "chica" | "mediana" | "grande";

@Component({
  selector: "sidenav",
  templateUrl: "sidenav.component.html",
  styleUrls: ["sidenav.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatMenuModule,
    MatListModule,
    RouterLink,
    TranslocoModule,
    MatToolbarModule,
    MatButtonModule,
    MatTooltipModule,
    RouterModule,
    TreeMenuComponent,
    OpenInNewTabDirective,
  ],
})
export class SidenavComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly adapter = inject(DateAdapter);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly changeTheme = inject(ChangeTheme);
  private readonly observer = inject(BreakpointObserver);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly rutaDinamicaService = inject(RutaDinamicaService);
  private readonly userState = inject(UserStateService);

  @HostBinding("class") public componentCssClass: string = "";

  public readonly routesTreeData = signal<TreeNode[]>([]);
  public readonly currentUrl = signal<string>("");
  public readonly toolbar = signal<boolean>(false);
  public readonly theme = signal<string>(localStorage.getItem("theme") || "light-theme");
  public readonly isMobile = signal<boolean>(false);
  /** Única fuente del estado del menú: atada con `[(opened)]`, así que también
   *  refleja los cierres que hace Material solo (ESC, backdrop en mobile). */
  public readonly abierto = signal<boolean>(false);
  public readonly rutaConSidenav = computed(() => this.esRutaConSidenav(this.currentUrl()));

  public readonly descnivelportal = this.userState.rol;
  public readonly dusuario = this.userState.dusuario;
  public readonly entorno = this.userState.entorno;

  /** En desktop el menú abierto no lleva botón; en mobile siempre, para poder cerrarlo. */
  public readonly showMenuButton = computed(
    () => this.rutaConSidenav() && (this.isMobile() || !this.abierto()),
  );

  /** En mobile el logo del toolbar está oculto por CSS, y con él el único acceso
   *  visible al dashboard de módulos. Este botón lo reemplaza mientras el usuario
   *  está dentro de un módulo. */
  public readonly showDashboardButton = computed(() => {
    if (!this.isMobile()) return false;
    const url = this.currentUrl();
    return !this.isExcludedRoute(url) && !this.isNoSidenavRoute(url);
  });

  public ngOnInit(): void {
    this.initTheme();
    this.initLanguage();
    this.listenToRouterEvents();
    this.listenToBreakpoints();
    this.listenToThemeChanges();
    this.listenToLanguageChanges();
  }

  public onSetTheme(themeName: string): void {
    const isDark = themeName === "dark-theme";
    const add = isDark ? "dark-theme" : "light-theme";
    const remove = isDark ? "light-theme" : "dark-theme";

    document.body.classList.remove(remove);
    document.body.classList.add(add);

    const overlay = this.overlayContainer.getContainerElement();
    overlay.classList.remove(remove);
    overlay.classList.add(add);

    this.theme.set(themeName);
    localStorage.setItem("theme", themeName);
    this.componentCssClass = themeName;
    this.cdr.markForCheck();
  }

  public alternarMenu(): void {
    this.abierto.update((abierto) => !abierto);
  }

  public logout(): void {
    this.authenticationService.logout();
    this.abierto.set(false);
  }

  @HostListener("contextmenu", ["$event"])
  public onRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  private initTheme(): void {
    const stored = localStorage.getItem("theme");
    if (stored && (stored === "dark-theme" || stored === "light-theme")) {
      this.onSetTheme(stored);
    } else {
      this.onSetTheme("light-theme");
    }
  }

  private initLanguage(): void {
    const lang = localStorage.getItem("activeLanguage");
    if (lang) {
      this.transloco.setActiveLang(lang);
    }
  }

  private listenToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        switchMap((event: NavigationEnd) => {
          const url = event.urlAfterRedirects || event.url;
          this.currentUrl.set(url);

          this.userState.initializeFromSession();

          this.toolbar.set(!this.isExcludedRoute(url) && url !== "/" && url !== "/#/");

          // En mobile el menú se cierra al navegar para que el contenido quede visible.
          this.abierto.set(this.rutaConSidenav() && !this.isMobile());

          return this.rutaDinamicaService.fetchMenuUsuario().pipe(
            tap((menu: MenuUsuario | null) => this.sincronizarModulo(url, menu)),
            catchError(() => EMPTY)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const routing = this.userState.routing();
        this.routesTreeData.set(
          this.rutaDinamicaService.getMenuTreeByName(routing || '')
        );
      });
  }

  /** El módulo sale de la URL: el que fija el dashboard se pierde con Atrás/Adelante o una URL pegada. */
  private sincronizarModulo(url: string, menu: MenuUsuario | null): void {
    const actual = this.userState.routing();
    const modulo = resolverModuloDeRuta(menu?.lista ?? [], url, actual);
    if (modulo && modulo !== actual) this.userState.setRouting(modulo);
  }

  private listenToBreakpoints(): void {
    this.observer
      .observe(["(max-width: 800px)", "(max-width: 1150px)"])
      .pipe(
        map((res): TamanioPantalla => {
          if (res.breakpoints["(max-width: 800px)"]) return "chica";
          return res.breakpoints["(max-width: 1150px)"] ? "mediana" : "grande";
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((pantalla: TamanioPantalla) => {
        this.isMobile.set(pantalla === "chica");
        this.abierto.set(pantalla === "grande" && this.rutaConSidenav());
      });
  }

  private listenToThemeChanges(): void {
    this.changeTheme.changeEmitted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: string) => this.onSetTheme(data));
  }

  private listenToLanguageChanges(): void {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang: string) => {
        const locale = lang === "en" ? "en" : "es-AR";
        this.adapter.setLocale(locale);
      });
  }

  /** La URL vacía es el arranque, antes del primer NavigationEnd: todavía no hay pantalla. */
  private esRutaConSidenav(url: string): boolean {
    if (!url || url === "/" || url === "/#/") return false;
    return !this.isExcludedRoute(url) && !this.isNoSidenavRoute(url);
  }

  private isExcludedRoute(url: string): boolean {
    return EXCLUDED_ROUTES.some((route) => url.includes(route));
  }

  private isNoSidenavRoute(url: string): boolean {
    return NO_SIDENAV_ROUTES.some((route) => url.includes(route));
  }
}
